/**
 * PrintDocumentBlock — timeline entry for the `print_document` tool.
 *
 * Visually a sibling of {@link ArtefactBlock} but with a prominent
 * "Print" button so the user can reopen the native print dialog after
 * the initial auto-fire (or on chat reload, when the dialog never fired
 * because the chunk came from persisted metadata rather than the
 * streaming pipeline).
 *
 * Web-only. Mobile never sees `print_document_item` because the
 * backend gates `PRINT_DOCUMENT_TOOL` on `is_mobile === false`.
 */

import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, Eye, Loader2, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useEvidenceAnswerPorts } from '../answer/ports';
import { ArtefactIllustration } from './ArtefactIllustration';

export interface PrintDocumentBlockProps {
  toolId: string;
  status: 'generating' | 'completed' | 'error';
  title: string;
  /** Presigned PDF URL when status is 'completed'. */
  url?: string;
  filename?: string;
  error?: string;
  /** Preview opens the shared artefact preview panel (mirrors ArtefactBlock). */
  onPreview?: () => void;
  className?: string;
}

export const PrintDocumentBlock = ({
  toolId,
  status,
  title,
  url,
  filename,
  error,
  onPreview,
  className,
}: PrintDocumentBlockProps): JSX.Element => {
  const { printDocument } = useEvidenceAnswerPorts();
  const [showIllustration, setShowIllustration] = useState(false);

  useEffect(() => {
    if (status === 'completed') {
      const timer = setTimeout(() => setShowIllustration(true), 300);
      return () => clearTimeout(timer);
    }
    setShowIllustration(false);
  }, [status]);

  // === GENERATING ===
  if (status === 'generating') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="print-document-block"
        data-status="generating"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <Loader2
              className="size-3 animate-spin text-text-tertiary"
              data-testid="loading-spinner"
            />
            <TypographyCaption className="text-text-tertiary">
              {/* eslint-disable-next-line formatjs/enforce-id -- minted while this file sat
                  in the ESLint-ignored Chat tree; the id is live in Crowdin across 17
                  locales, so re-minting it to the hash would orphan every translation. */}
              <FormattedMessage
                defaultMessage="Preparing document for printing…"
                id="chat.printDocument.generating"
              />
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // === ERROR ===
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="print-document-block"
        data-status="error"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <AlertCircle
              className="size-3 text-validation-destructive"
              data-testid="error-icon"
            />
            <TypographyCaption className="text-validation-destructive">
              {error || (
                /* eslint-disable-next-line formatjs/enforce-id -- minted while this file sat
                   in the ESLint-ignored Chat tree; the id is live in Crowdin across 17
                   locales, so re-minting it to the hash would orphan every translation. */
                <FormattedMessage
                  defaultMessage="Failed to prepare document for printing."
                  id="chat.printDocument.failed"
                />
              )}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // === COMPLETED ===
  // Note: the 24-hour S3 presigned URL will eventually expire. When it
  // does, the iframe load will 404 and the print dialog won't open.
  // The button is still shown so the user sees it existed — we rely on
  // the downstream telemetry and the PDF-export regeneration flow to
  // recover if that matters.
  const handlePrint = () => {
    if (!url) return;
    printDocument(url, { toolId });
  };

  const e2ePdfUrlAttr =
    typeof window !== 'undefined' &&
    (window as Window & { __E2E_HOOKS__?: boolean }).__E2E_HOOKS__ === true &&
    url
      ? { 'data-pdf-url': url }
      : undefined;

  return (
    <div
      className={cn(
        'group relative flex w-full items-start gap-3 overflow-hidden rounded-lg border p-4',
        'transition-all duration-200',
        'bg-surface-accent hover:bg-surface-accent-hover',
        'border-border hover:border-border-hover',
        className
      )}
      data-testid="print-document-block"
      data-status="completed"
      {...e2ePdfUrlAttr}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <TypographyP2 className="line-clamp-2 max-w-48 font-medium text-text-primary sm:max-w-xs md:max-w-md lg:max-w-lg">
          {title}
        </TypographyP2>

        {filename && (
          <TypographyCaption className="line-clamp-2 max-w-48 text-text-tertiary sm:max-w-xs md:max-w-md lg:max-w-lg">
            {filename}
          </TypographyCaption>
        )}

        <div className="mt-2 flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            onClick={handlePrint}
            disabled={!url}
            data-testid="print-document-button"
          >
            <Printer />
            {/* eslint-disable-next-line formatjs/enforce-id -- minted while this file sat
                in the ESLint-ignored Chat tree; the id is live in Crowdin across 17
                locales, so re-minting it to the hash would orphan every translation. */}
            <FormattedMessage defaultMessage="Print" id="chat.printDocument.print" />
          </Button>
          {onPreview && (
            <Button
              size="sm"
              variant="outline"
              onClick={onPreview}
              disabled={!url}
              data-testid="print-document-preview-button"
            >
              <Eye />
              {/* eslint-disable-next-line formatjs/enforce-id -- minted while this file sat
                  in the ESLint-ignored Chat tree; the id is live in Crowdin across 17
                  locales, so re-minting it to the hash would orphan every translation. */}
              <FormattedMessage
                defaultMessage="Preview"
                id="chat.printDocument.preview"
              />
            </Button>
          )}
        </div>
      </div>

      <div
        className={cn(
          'absolute right-8 top-4',
          'transition-all duration-300',
          showIllustration ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        )}
        style={{ transitionTimingFunction: 'cubic-bezier(.215, .61, .355, 1)' }}
        aria-hidden="true"
      >
        <ArtefactIllustration type="pdf" className="h-auto w-[180px]" />
      </div>
    </div>
  );
};
